import React from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Accordion as DSAccordion, Heading } from '@frontend/design-system';
import { AccordionId, useAccordionContext } from '../../context/AccordionContext';

type AccordionProps = {
  accordionTitle: string;
  accordionId: AccordionId;
  children: React.ReactNode;
};

export const Accordion = React.memo(({ accordionId, children, accordionTitle }: AccordionProps) => {
  const { activeAccordionIds, toggleAccordion } = useAccordionContext();
  const isActive = activeAccordionIds.has(accordionId);
  const handleAccordionChange = () => {
    toggleAccordion(accordionId);
  };

  return (
    <DSAccordion
      css={{ borderRadius: theme.borderRadius.medium }}
      onChange={handleAccordionChange}
      padding={[0, 0, 0, 2]}
      chevronSize={16}
      controlledValue={isActive ? accordionId : null}
    >
      <DSAccordion.Item css={itemStyles} value={accordionId}>
        <DSAccordion.Header css={headerStyles}>
          <Heading css={headingStyles} level={3}>
            {accordionTitle}
          </Heading>
        </DSAccordion.Header>
        <DSAccordion.Body css={bodyStyles}>{children}</DSAccordion.Body>
      </DSAccordion.Item>
    </DSAccordion>
  );
});

Accordion.displayName = 'Accordion';

const itemStyles = css`
  overflow: visible;
  box-shadow: none;
  margin-bottom: 0;
  h3 button[aria-expanded='false'] {
    border-bottom-left-radius: ${theme.borderRadius.medium};
    border-bottom-right-radius: ${theme.borderRadius.medium};
  }
`;
const headerStyles = css`
    background-color: ${theme.colors.primary5};
    border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0;
    border: 1px solid ${theme.colors.primary20};
}
`;
const headingStyles = css`
  padding-left: ${theme.spacing(2)};
  padding: ${theme.spacing(0)};
`;

const bodyStyles = css`
  border: 1px solid ${theme.colors.neutral20};
  border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
  border-top: none;
`;
